/**
 * Libraries
 */

import apolloClient from '@core/apollo';
import { ApolloError } from '@apollo/client';

/**
 * Queries
 */

import { TICKET_BY_CODE } from '@core/apollo/queries/ticket';

/**
 * Types
 */

import type { Ticket } from '@generatedTypes';
import type { CodeLink } from '@shared/types/Link';

const ticketByCodeErrorCode = {
  NOT_FOUND: 'NOT_FOUND',
  FORBIDDEN: 'FORBIDDEN',
} as const;

type TicketLoadFailure =
  | { status: 'forbidden' }
  | { status: 'not_found' }
  | { status: 'technical'; cause: unknown };

type TicketLoadResult =
  | { status: 'loaded'; ticket: Ticket }
  | TicketLoadFailure;

/* Classifies a ticket load error into a TicketLoadFailure.
 * @param error - The error to classify.
 * @returns A TicketLoadFailure.
 */
function classifyTicketLoadError(error: unknown): TicketLoadFailure {
  if (error instanceof ApolloError) {
    const extensionCode = error.graphQLErrors[0]?.extensions?.code;

    if (extensionCode === ticketByCodeErrorCode.NOT_FOUND) {
      return { status: 'not_found' };
    }

    if (extensionCode === ticketByCodeErrorCode.FORBIDDEN) {
      return { status: 'forbidden' };
    }
  }

  return { status: 'technical', cause: error };
}

/*
 * Loads a ticket by code from the Apollo cache or network.
 * @param code - The code of the ticket to load.
 * @param shouldFetchTicketFromNetwork - Whether to fetch the ticket from the network.
 */
async function loadTicketByCode(
  code: CodeLink,
  shouldFetchTicketFromNetwork: boolean,
): Promise<TicketLoadResult> {
  const fetchPolicy = shouldFetchTicketFromNetwork
    ? 'network-only'
    : 'cache-first';

  try {
    const { data } = await apolloClient.query({
      query: TICKET_BY_CODE,
      variables: { code },
      fetchPolicy,
    });

    return { status: 'loaded', ticket: data.ticketByCode };
  } catch (error) {
    return classifyTicketLoadError(error);
  }
}

export {
  ticketByCodeErrorCode,
  classifyTicketLoadError,
  loadTicketByCode,
  type TicketLoadFailure,
  type TicketLoadResult,
};
